import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PenTool, Upload, X, Check } from "lucide-react";
import { PurchaseOrderData, PurchaseOrderValidation } from "@/components/campaign/types/purchaseOrderV2";
import SignaturePad from "@/components/campaign/components/SignaturePad";
import { format } from "date-fns";

interface SignatureStepProps {
  purchaseOrderData: PurchaseOrderData;
  validation: PurchaseOrderValidation;
  onSignatureSave: (signatureData: string, type: 'draw' | 'upload') => void;
  onStampUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onStampRemove: () => void;
}

export const SignatureStep = ({ 
  purchaseOrderData, 
  onSignatureSave, 
  onStampUpload,
  onStampRemove 
}: SignatureStepProps) => {
  const { signature, stamp } = purchaseOrderData;

  return (
    <div className="space-y-6">
      {/* Digital Signature */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <PenTool className="h-5 w-5" />
            <span>Digital Signature</span>
            {signature && (
              <Badge variant="outline" className="ml-auto">
                <Check className="h-3 w-3 mr-1" />
                Completed
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Please provide your digital signature to authorize this purchase order.
          </p>
          
          <SignaturePad onSave={onSignatureSave} />
          
          {signature && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-800">Signature Captured</p>
                  <p className="text-xs text-green-600">
                    {signature.type === 'draw' ? 'Hand-drawn' : 'Uploaded'} • 
                    {format(new Date(signature.timestamp), 'PPp')}
                  </p>
                </div>
                <Check className="h-5 w-5 text-green-600" />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Company Stamp */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Upload className="h-5 w-5" />
            <span>Company Stamp</span>
            {stamp && (
              <Badge variant="outline" className="ml-auto">
                <Check className="h-3 w-3 mr-1" />
                Uploaded
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Upload your official company stamp to validate this purchase order.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <input
                type="file"
                accept="image/*"
                onChange={onStampUpload}
                className="text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer w-full"
              />
              <p className="text-xs text-muted-foreground">
                Supported formats: PNG, JPG, JPEG (Max 5MB)
              </p>
            </div>
            
            {stamp && (
              <div className="relative">
                <div className="border border-border rounded-lg p-4 bg-background">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={onStampRemove}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                  <img 
                    src={stamp} 
                    alt="Company stamp" 
                    className="max-h-24 max-w-full object-contain mx-auto"
                  />
                </div>
                <p className="text-xs text-center text-muted-foreground mt-2">
                  Company stamp uploaded
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-sm font-bold">i</span>
            </div>
            <div className="space-y-2">
              <h4 className="font-medium text-blue-900">Important Information</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Your digital signature legally binds you to this purchase order</li>
                <li>• The company stamp validates the authenticity of this document</li>
                <li>• Both signature and stamp are required to proceed</li>
                <li>• This information will be included in the final purchase order document</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};